import { gql, useMutation, useLazyQuery } from '@apollo/client';
import { Pda } from './components/Pda';
import { Header } from './components/Header';
import { useCallback, useRef, useState } from 'react';
import { Column, createRecord, insertRecordBySeq, ResultTable, ScanRecord } from './components/ResultTable';
import { ScanInput } from './components/ScanInput';
import {isTrackingNumber, isValidBarCode, showBarCodeError, showTrackingNumberError} from 'utils/BarCodeUtil';
import { useScanQueueConsumer } from './components/useScanQueueConsumer';
import { parseException, showError, showRepeatedScanError, parseExceptionMessageKey } from 'utils/exceptionUtils';
import { playScanFailedAoSound } from 'utils/scanUtils';
import { AutoComplete } from './components/AutoComplete';
import { GET_WAREHOUSE_BY_DESTINATION, GetWarehouseByDestinationResponse, GetWarehouseByDestinationVariables } from '../../graphql/queries/warehouseQueries';
import { t } from '../../i18n';
import {formatLocalizedTime} from "../../utils/dateFormatUtils";
import { SortingScanOverlay, SortingScanResponse } from './components/SortingScanOverlay';
import { parseSwiftXException } from 'graphql/types/SwiftXException';
import { getClientReporter } from '../../utils/clientReport/clientReporterRegistry';
import { ClientReportOpType } from '../../utils/clientReport/clientReportTypes';
import { safeJsonStringify, truncateToLength } from '../../utils/clientReport/clientReportUtils';


const HANDLE_WHS_SORTING_SCAN_EVENT = gql`
mutation HandleWhsSortingScanEvent($destinations: [String!]!, $barCode: String!, $clientReportKey: String) {
  result: handleWhsSortingScanEvent(destinations: $destinations, barCode: $barCode, clientReportKey: $clientReportKey) {
    success
    exception {
      errorCode
      messageKey
      message
      details
    }
    carrier
    sortingCode
    trackingNumber
    dspName
  }
}
`;

interface HandleScanResult {
    result: SortingScanResponse;
}

const PageBody = () => {
    const sortingScanClientReporter = getClientReporter(ClientReportOpType.SORTING_SCAN);

    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
    const [destinations, setDestinations] = useState<string[]>([]);
    const [destinationInput, setDestinationInput] = useState('');
    const [destinationOptions, setDestinationOptions] = useState<Array<{label: string, value: string}>>([]);
    const [scanValue, setScanValue] = useState('');
    const value2Ref = useRef<HTMLInputElement>(null);
    const [overlayVisible, setOverlayVisible] = useState(false);
    const [overlayData, setOverlayData] = useState<SortingScanResponse>();

    const [handleWhsSortingScanEvent] = useMutation<HandleScanResult>(HANDLE_WHS_SORTING_SCAN_EVENT);

    const [getWarehouseByDestination, { loading: destinationLoading }] = useLazyQuery<
        GetWarehouseByDestinationResponse,
        GetWarehouseByDestinationVariables
    >(GET_WAREHOUSE_BY_DESTINATION, {
        onCompleted: (data) => {
            const warehouses = data.getWarehouseByDestination || [];

            if (warehouses.length === 0) {
                showError({
                    title: t('pda.common.error'),
                    message: t('pda.pages.sorting.destinationNotFound')
                });
                setDestinationOptions([]);
                return;
            }

            // 显示选项供用户选择
            const options = warehouses.map(item => ({
                label: item,
                value: item
            }));
            setDestinationOptions(options);
        },
        onError: (error) => {
            setDestinationOptions([]);
        }
    });

    const destinationsRef = useRef<string[]>(destinations);
    destinationsRef.current = destinations;

    const onProcess = useCallback(async (event: { id: string; seq: number; code: string; ts: number }) => {
        const barCode = event.code;
        const clientReportKey = sortingScanClientReporter.createReportKey(barCode);
        void sortingScanClientReporter.onScanTriggered(clientReportKey, barCode);
        if (!isTrackingNumber(barCode)) {
            showTrackingNumberError(barCode);
            void sortingScanClientReporter.onIntercepted(clientReportKey, `invalid_tracking_number:${barCode}`);
            return;
        }

        value2Ref.current?.blur();
        setScanValue(barCode);

        const currentDestinations = destinationsRef.current;

        // 检查是否有选中的destinations
        if (currentDestinations.length === 0) {
            const message = t('pda.pages.sorting.pleaseSelectDestination');
            playScanFailedAoSound();
            showError({title: t('pda.common.error'), message});
            void sortingScanClientReporter.onIntercepted(clientReportKey, message);
            setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                value2: barCode,
                failedReason: message,
                time: formatLocalizedTime(),
                seq: event.seq
            })));
            return;
        }

        // 同步处理扫描请求
        void sortingScanClientReporter.onRequest(clientReportKey, { destinations: currentDestinations, barCode });
        let responseRecorded = false;
        let responseData: any = null;
        try {
            const {data} = await handleWhsSortingScanEvent({variables: {destinations: currentDestinations, barCode, clientReportKey}});
            const success = data?.result?.success || false;

            if (success) {
                responseData = data?.result;
                sortingScanClientReporter.onResponse(clientReportKey, responseData)
                    .then(() => {
                        responseRecorded = true;
                    })
                    .catch((_) => {
                        // IndexedDB 写入失败，直接触发上报
                        void sortingScanClientReporter.reportDirectly(clientReportKey, {
                            responseAtMs: Date.now(),
                            responseResult: truncateToLength(safeJsonStringify(responseData), 300)
                        });
                        responseRecorded = true; // 标记已处理，避免 finally 重复上报
                    });
                setSuccessRecords(prev => insertRecordBySeq(prev, createRecord({
                    value2: barCode,
                    time: formatLocalizedTime(),
                    seq: event.seq
                })));
                setOverlayData(data?.result || undefined);
                setOverlayVisible(true);
            } else {
                playScanFailedAoSound();
                const {title, message} = parseSwiftXException(data?.result?.exception);
                responseData = data?.result;
                sortingScanClientReporter.onResponse(clientReportKey, responseData)
                    .then(() => {
                        responseRecorded = true;
                    })
                    .catch((_) => {
                        // IndexedDB 写入失败，直接触发上报
                        void sortingScanClientReporter.reportDirectly(clientReportKey, {
                            responseAtMs: Date.now(),
                            responseResult: truncateToLength(safeJsonStringify(responseData), 300)
                        });
                        responseRecorded = true; // 标记已处理，避免 finally 重复上报
                    });
                showError({title: title || t('pda.common.scanFailed'), message: message || t('pda.common.scanFailed')});
                setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                    value2: barCode,
                    failedReason: message || t('pda.common.scanFailed'),
                    time: formatLocalizedTime(),
                    seq: event.seq
                })));
                setOverlayData(data?.result);
                if (data?.result?.carrier) {
                    setOverlayVisible(true);
                }
            }
        } catch (error: any) {
            const messageKey = parseExceptionMessageKey(error);
            let message: string;
            if (messageKey === "errors.business.alreadyScanned") {
                showRepeatedScanError(error);
                const { message: errorMessage } = parseException(error);
                message = errorMessage;
            } else {
                const {title, message: errorMessage} = parseException(error);
                playScanFailedAoSound();
                showError({title, message: errorMessage});
                message = errorMessage;
            }
            responseData = { error: String(error), messageKey, message };
            sortingScanClientReporter.onResponse(clientReportKey, responseData)
                .then(() => {
                    responseRecorded = true;
                })
                .catch((_) => {
                    // IndexedDB 写入失败，直接触发上报
                    void sortingScanClientReporter.reportDirectly(clientReportKey, {
                        responseAtMs: Date.now(),
                        responseResult: truncateToLength(safeJsonStringify(responseData), 300)
                    });
                });
            setFailedRecords(prev => insertRecordBySeq(prev, createRecord({
                value2: barCode,
                failedReason: message,
                time: formatLocalizedTime(),
                seq: event.seq
            })));
        } finally {
            if (!responseRecorded) {
                sortingScanClientReporter.onResponse(clientReportKey, responseData || {})
                    .catch((_) => {
                        // IndexedDB 写入失败，直接触发上报
                        void sortingScanClientReporter.reportDirectly(clientReportKey, {
                            responseAtMs: Date.now(),
                            responseResult: responseData ? truncateToLength(safeJsonStringify(responseData), 300) : ''
                        });
                    });
            }
        }
    }, []);

    const { enqueueScan } = useScanQueueConsumer({ maxConcurrency: 1, onProcess });

    const successColumns: Column[] = [
        { title: t('pda.common.trackingNO'), key: 'value2' },
        { title: t('pda.common.time'), key: 'time' }
    ];

    const failedColumns: Column[] = [
        ...successColumns,
        { title: t('pda.common.failedReason'), key: 'failedReason' }
    ];

    const handleDestinationInputChange = (value: string) => {
        setDestinationInput(value);

        if (value.trim()) {
            getWarehouseByDestination({
                variables: { destination: value.trim() }
            });
        } else {
            setDestinationOptions([]);
        }
    };

    const handleDestinationSelect = (value: string) => {
        const trimmedValue = value.trim();
        if (trimmedValue && !destinations.includes(trimmedValue)) {
            setDestinations(prev => [...prev, trimmedValue]);
            setDestinationInput('');
            setDestinationOptions([]);
        }
    };

    const removeDestination = (index: number) => {
        setDestinations(prev => prev.filter((_, i) => i !== index));
    };

    const handleOverlayClose = () => {
        setOverlayVisible(false);
        setOverlayData(undefined);
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="mb-2">
                <AutoComplete
                    className="w-full p-2 border rounded"
                    placeholder={t('pda.pages.sorting.destination')}
                    value={destinationInput}
                    onChange={handleDestinationInputChange}
                    onSelect={handleDestinationSelect}
                    options={destinationOptions}
                />
                {destinations.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {destinations.map((dest, index) => (
                            <div key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                                <span>{dest}</span>
                                <button
                                    onClick={() => removeDestination(index)}
                                    className="text-blue-600 hover:text-blue-800 font-bold ml-1"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <ScanInput
                ref={value2Ref}
                placeholder={t('pda.pages.sorting.trackingNO')}
                value={scanValue}
                onScan={(code: string) => {
                    if (isValidBarCode(code)) {
                        enqueueScan(code);
                    }
                }}
            />
            <ResultTable
                successRecords={successRecords}
                failedRecords={failedRecords}
                successColumns={successColumns}
                failedColumns={failedColumns}
            />
            {overlayData && (
                <SortingScanOverlay
                    visible={overlayVisible}
                    data={overlayData}
                    onClose={handleOverlayClose}
                />
            )}
        </div>
    );
}

export default function SortingScanPage() {
    return (
        <div className="w-full h-full flex flex-col gap-2 p-2">
            <Header title={t('pda.pages.sorting.sortingScan')} />
            <Pda />
            <PageBody />
        </div>
    );
}